import { io } from 'socket.io-client';
import { getSocketUrl } from './utils/platform';

let socket = null;
let connectionPromise = null;

const SOCKET_URL = getSocketUrl();

export function connectSocket(token) {
  if (socket?.connected) return socket;

  // Clean up stale socket
  if (socket) {
    socket.removeAllListeners();
    socket.disconnect();
    socket = null;
  }

  socket = io(SOCKET_URL, {
    auth: { token },
    transports: ['websocket', 'polling'],
    reconnectionAttempts: 8,
    reconnectionDelay: 1000,
    timeout: 10000
  });

  connectionPromise = new Promise((resolve, reject) => {
    const timer = setTimeout(() => {
      reject(new Error('Connection timed out'));
    }, 15000);

    socket.on('connect', () => {
      clearTimeout(timer);
      console.log('Socket connected:', socket.id);
      resolve(socket);
    });

    socket.on('connect_error', (err) => {
      console.error('Socket connection error:', err.message);
    });

    socket.io.on('reconnect_failed', () => {
      clearTimeout(timer);
      reject(new Error('Failed to connect after multiple attempts'));
    });
  });
  // Prevent unhandled rejection — errors are surfaced via socketStatus state
  connectionPromise.catch(() => {});

  return socket;
}

export function getSocket() {
  return socket;
}

export function isConnected() {
  return socket?.connected || false;
}

export function disconnectSocket() {
  if (socket) {
    socket.removeAllListeners();
    socket.disconnect();
    socket = null;
    connectionPromise = null;
  }
}

// Wait for socket to be connected (with timeout)
async function waitForConnection() {
  if (socket?.connected) return true;
  if (!connectionPromise) return false;
  try {
    await connectionPromise;
    return socket?.connected || false;
  } catch {
    return false;
  }
}

export async function createRoom(playerCount, aiCount, gameMode = 'buck', partnerMode = 'fixed', isPrivate = false) {
  const connected = await waitForConnection();
  if (!connected) return { success: false, error: 'Cannot connect to server. Please refresh and try again.' };
  return new Promise((resolve) => {
    socket.emit('create-room', { playerCount, aiCount, gameMode, partnerMode, isPrivate }, resolve);
  });
}

export async function joinRoom(roomCode) {
  const connected = await waitForConnection();
  if (!connected) return { success: false, error: 'Cannot connect to server. Please refresh and try again.' };
  return new Promise((resolve) => {
    socket.emit('join-room', { roomCode }, resolve);
  });
}

export async function startGame() {
  const connected = await waitForConnection();
  if (!connected) return { success: false, error: 'Cannot connect to server. Please refresh and try again.' };
  return new Promise((resolve) => {
    socket.emit('start-game', null, resolve);
  });
}

export function sendGameState(state) {
  socket?.emit('game-state', state);
}

export function sendPlayerAction(action) {
  socket?.emit('player-action', action);
}

export function sendChatMessage(text) {
  socket?.emit('chat-message', { text });
}

export async function spectateRoom(roomCode) {
  const connected = await waitForConnection();
  if (!connected) return { success: false, error: 'Cannot connect to server. Please refresh and try again.' };
  return new Promise((resolve) => {
    socket.emit('join-as-spectator', { roomCode }, resolve);
  });
}

export function leaveRoom() {
  socket?.emit('leave-room');
}

// Regional chat
export async function joinRegionalChat(region) {
  const connected = await waitForConnection();
  if (!connected) return { success: false, error: 'Cannot connect to server.' };
  return new Promise((resolve) => {
    socket.emit('join-regional-chat', { region }, resolve);
  });
}

export function sendRegionalChatMessage(region, text) {
  socket?.emit('regional-chat-message', { region, text });
}

export function leaveRegionalChat(region) {
  socket?.emit('leave-regional-chat', { region });
}
